import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { UserPicker } from '../../components/UserPicker.js';

interface RiskRow {
  readonly riskId: string;
  readonly riskCode: string;
  readonly riskName: string;
  readonly riskCategory: string;
  readonly probability: number;
  readonly impact: number;
  readonly riskScore: number;
  readonly riskLevel: string;
  readonly status: string;
}

export function M21RiskRegisterPage(): ReactNode {
  const { number } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<RiskRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [riskCode, setRiskCode] = useState('');
  const [riskName, setRiskName] = useState('');
  const [riskCategory, setRiskCategory] = useState('');
  const [description, setDescription] = useState('');
  const [probability, setProbability] = useState('3');
  const [impact, setImpact] = useState('3');
  const [riskLevel, setRiskLevel] = useState('MEDIUM');
  const [ownerId, setOwnerId] = useState('');

  function load(): void {
    apiRequest<{ data: RiskRow[] }>('/v1/risk', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: RiskRow[] }>('/v1/risk', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setActionError(null);
    if (!riskCode.trim() || !riskName.trim() || !riskCategory.trim() || !description.trim() || !ownerId.trim()) {
      setActionError(t('m21.risk.validationRequired'));
      return;
    }
    setBusyId('create');
    try {
      await apiRequest('/v1/risk', {
        method: 'POST',
        token,
        body: {
          riskCode: riskCode.trim(),
          riskName: riskName.trim(),
          riskCategory: riskCategory.trim(),
          description: description.trim(),
          probability: Number(probability),
          impact: Number(impact),
          riskLevel,
          ownerId: ownerId.trim(),
        },
      });
      setRiskCode(''); setRiskName(''); setRiskCategory(''); setDescription('');
      setProbability('3'); setImpact('3'); setRiskLevel('MEDIUM'); setOwnerId('');
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function review(id: string, current: number): Promise<void> {
    setActionError(null);
    setBusyId(`review-${id}`);
    try {
      await apiRequest(`/v1/risk/${id}/review`, {
        method: 'POST',
        token,
        body: { reviewDate: new Date().toISOString().slice(0, 10), currentScore: current },
      });
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m21.risk.loading')}</p>;
  return (
    <main aria-label="risk-register">
      <h1>{t('m21.risk.title')}</h1>

      <form aria-label="risk-create-form" onSubmit={handleCreate}>
        <label>{t('m21.risk.formCode')}<input aria-label="Risk code" value={riskCode} onChange={(e) => setRiskCode(e.target.value)} /></label>
        <label>{t('m21.risk.formName')}<input aria-label="Risk name" value={riskName} onChange={(e) => setRiskName(e.target.value)} /></label>
        <label>{t('m21.risk.formCategory')}<input aria-label="Risk category" value={riskCategory} onChange={(e) => setRiskCategory(e.target.value)} /></label>
        <label>{t('m21.risk.formDescription')}<input aria-label="Risk description" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        <label>{t('m21.risk.formProbability')}<input aria-label="Probability" type="number" min="1" max="5" value={probability} onChange={(e) => setProbability(e.target.value)} /></label>
        <label>{t('m21.risk.formImpact')}<input aria-label="Impact" type="number" min="1" max="5" value={impact} onChange={(e) => setImpact(e.target.value)} /></label>
        <label>{t('m21.risk.formLevel')}
          <select aria-label="Risk level" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
            <option value="CRITICAL">CRITICAL</option>
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
          </select>
        </label>
        <UserPicker label={t('m21.risk.formOwner')} testId="risk-owner" value={ownerId} onPick={(id) => setOwnerId(id)} />
        <button type="submit" disabled={busyId === 'create'}>{t('m21.risk.createButton')}</button>
      </form>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="risk-register-table">
        <thead><tr><th>{t('m21.risk.colCode')}</th><th>{t('m21.risk.colName')}</th><th>{t('m21.risk.colCategory')}</th><th>{t('m21.risk.colProbability')}</th><th>{t('m21.risk.colImpact')}</th><th>{t('m21.risk.colScore')}</th><th>{t('m21.risk.colLevel')}</th><th>{t('m21.risk.colStatus')}</th><th>{t('m21.risk.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.riskId}>
              <td>{r.riskCode}</td>
              <td>{r.riskName}</td>
              <td>{r.riskCategory}</td>
              <td>{r.probability}</td>
              <td>{r.impact}</td>
              <td>{number(r.riskScore)}</td>
              <td>{r.riskLevel}</td>
              <td>{r.status}</td>
              <td>
                <button
                  type="button"
                  aria-label={`review-${r.riskId}`}
                  disabled={busyId === `review-${r.riskId}`}
                  onClick={() => review(r.riskId, r.riskScore)}
                >{t('m21.risk.assess')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
